import { ChangeDetectionStrategy, Component, HostListener, inject, signal } from '@angular/core';
import { PAGE_RELOAD } from '@/app/core/browser/page-reload.token';
import { EntitlementService } from '@/app/core/entitlements/entitlement.service';
import { NavIcons } from '@/app/shared/nav/nav-icons';
import { SafeHtmlPipe } from '@/app/shared/ui/pipes/safe-html.pipe';
import { ToastService } from '@/app/shared/ui/notifications/toast.service';

@Component({
  selector: 'app-organization-selector',
  standalone: true,
  imports: [SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (organizations().length > 0) {
      <div class="relative">
        <button
          type="button"
          class="flex items-center gap-2 rounded-sm border-[0.5px]  py-1.5 pl-3.5 pr-2.5
          text-sm font-semibold text-white transition-colors duration-150 hover:bg-white/15 !border !border-border/20"
          [attr.aria-expanded]="menuOpen()"
          aria-haspopup="true"
          (click)="toggleMenu()"
        >
          {{ activeOrganizationName() }}
          <span
            class="flex shrink-0 text-white transition-transform duration-150 [&_svg]:h-4 [&_svg]:w-4"
            [class.rotate-180]="menuOpen()"
            [innerHTML]="icons.chevronDown | safeHtml"
          ></span>
        </button>

        @if (menuOpen()) {
          <button
            type="button"
            class="fixed inset-0 z-[90] cursor-default border-0 bg-transparent p-0"
            aria-label="Cerrar selector de organización"
            (click)="closeMenu()"
          ></button>
          <div
            class="absolute right-0 top-[calc(100%+0.5rem)] z-[100] w-72 overflow-hidden rounded-sm border border-border bg-surface shadow"
            role="menu"
          >
            <div
              class="border-b border-border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted"
            >
              Holding: {{ holdingName() }} — Cambiar de clínica
            </div>
            @for (org of organizations(); track org.id) {
              <button
                type="button"
                class="flex w-full items-center justify-between gap-3 border-0 bg-transparent px-4 py-2.5 text-left text-sm hover:bg-surface-2"
                [class.text-primary]="org.id === activeOrganizationId()"
                [class.font-semibold]="org.id === activeOrganizationId()"
                [class.text-heading]="org.id !== activeOrganizationId()"
                role="menuitemradio"
                [attr.aria-checked]="org.id === activeOrganizationId()"
                (click)="selectOrganization(org.id)"
              >
                <span class="truncate">
                  {{ org.name }}
                  @if (org.rut) {
                    <span class="text-xs font-semibold uppercase tracking-[0.04em] text-muted"
                      >RUT {{ org.rut }}</span
                    >
                  }
                </span>
                @if (org.id === activeOrganizationId()) {
                  <span
                    class="shrink-0 [&_svg]:h-4 [&_svg]:w-4"
                    [innerHTML]="icons.check | safeHtml"
                  ></span>
                }
              </button>
            }
          </div>
        }
      </div>
    }
  `,
})
export class OrganizationSelectorComponent {
  private readonly entitlements = inject(EntitlementService);
  private readonly reloadPage = inject(PAGE_RELOAD);
  private readonly toasts = inject(ToastService);
  protected readonly icons = NavIcons;
  protected readonly menuOpen = signal(false);

  protected readonly organizations = this.entitlements.organizations;
  protected readonly activeOrganizationId = this.entitlements.activeOrganizationId;
  protected readonly holdingName = () => this.entitlements.holding()?.name ?? '';
  protected readonly activeOrganizationName = () =>
    this.organizations().find((org) => org.id === this.activeOrganizationId())?.name ?? '';

  protected toggleMenu(): void {
    this.menuOpen.update((open) => !open);
  }

  protected closeMenu(): void {
    this.menuOpen.set(false);
  }

  protected selectOrganization(organizationId: string): void {
    this.closeMenu();
    if (organizationId === this.activeOrganizationId()) return;
    this.entitlements.switchOrganization(organizationId).subscribe({
      next: () => this.reloadPage(),
      error: () =>
        this.toasts.error('No se pudo cambiar de clínica', {
          description: 'Inténtalo nuevamente en unos minutos.',
        }),
    });
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.closeMenu();
  }
}
